import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const InternalButton = ({
  disabled,
  isWide,
  title,
  children,
  classNames,
  wrapperClassNames,
  action,
  onFocusLost,
}) => (
  <button
    disabled={disabled}
    title={title}
    aria-label={title}
    className={[
      "h-auto p-2 whitespace-nowrap cursor-pointer   rounded border-none    bg-secondary-main_button_bg  hover:bg-secondary-main_button_hover active:scale-98 transition-all duration-300 ease-out disabled:pointer-events-none disabled:opacity-50 active:transform scale-[0.98] active:transition-all active:duration-300 active:ease-out",
      isWide ? "w-full" : "w-max",
      ...(classNames ?? []),
    ].join(" ")}
    onClick={action}
    onBlur={onFocusLost}>
    <div className={(wrapperClassNames ?? []).join(" ")}>{children}</div>
  </button>
);

const ActionsButton = ({
  title,
  disabled,
  isWide,
  children,
  classNames,
  actions,
  optionsWidth = "default",
  optionsPosition = "bottom",
  customMenuContainerRenderer,
  customOptionsContainerRenderer,
}) => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);

  const props = {
    title: title,
    disabled: disabled,
    classNames: classNames,
    content: children,
    action: () => setIsMenuOpened((prev) => !prev),
    onFocusLost: () => {
      setTimeout(() => setIsMenuOpened(false), 300);
    },
  };
  if (actions.length === 1) {
    const action = actions[0];
    props.classNames = [...(classNames ?? []), ...(action.classNames ?? [])];
    props.title = action.title ?? props.title;
    props.disabled = props.disabled || action.disabled;
    props.content = action.content;
    props.action = action.action ?? (() => null);
    props.onFocusLost = () => null;
  }

  useEffect(() => {
    if (!isMenuOpened || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const style = { position: "fixed", zIndex: 9999, minWidth: rect.width };
    if (optionsPosition === "top") {
      style.bottom = window.innerHeight - rect.top;
    } else {
      style.top = rect.bottom + 2;
    }
    if (optionsWidth === "to-left") {
      style.right = window.innerWidth - rect.right;
    } else if (optionsWidth === "centered") {
      style.left = rect.left + rect.width / 2;
      style.transform = "translateX(-50%)";
    } else {
      style.left = rect.left;
    }
    setDropdownStyle(style);
  }, [isMenuOpened, optionsPosition, optionsWidth]);

  const defaultContainerRenderer = (content) => <>{content}</>;
  const menuContainerRenderer = customMenuContainerRenderer ?? defaultContainerRenderer;
  const optionsContainerRenderer = customOptionsContainerRenderer ?? defaultContainerRenderer;

  const dropdown =
    isMenuOpened && actions.length > 1
      ? menuContainerRenderer(
          optionsContainerRenderer(
            <div
              className="options box-border max-h-[300px] overflow-y-auto text-black bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.13)] border border-gray-100 w-max py-1.5"
              style={dropdownStyle}>
              {actions.map((x, i) => (
                <div
                  key={i}
                  className={[
                    "option flex items-center gap-2.5 w-full px-4 py-2.5 cursor-pointer text-left text-sm font-medium transition-colors duration-150 hover:bg-gray-50 active:bg-gray-100 select-none",
                    x.disabled ? "disabled cursor-not-allowed opacity-40 pointer-events-none" : "",
                    ...(x.classNames ?? []),
                  ].join(" ")}
                  onClick={x.disabled || !x.action ? undefined : x.action}>
                  {x.content}
                </div>
              ))}
            </div>,
          ),
        )
      : null;

  return (
    <div className="ui-actions-button relative" ref={buttonRef}>
      <InternalButton
        title={props.title}
        disabled={props.disabled}
        isWide={isWide}
        classNames={["actions", ...(props.classNames ?? [])]}
        action={props.action}
        wrapperClassNames={
          actions.length > 1 ? [] : ["ui-actions-button-action-has-only-child justify-center "]
        }
        onFocusLost={props.onFocusLost}>
        <div>{props.content}</div>
        {actions.length > 1 ? <div>DropdownIcon</div> : <></>}
      </InternalButton>
      {dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
};

export const PrimaryActionsButton = ({ classNames, children, ...restProps }) => (
  <ActionsButton
    classNames={[
      "primary active:scale-90 bg-secondary-primary_button_bg text-secondary-primary_text hover:bg-secondary-primary_button_hover",
      ...(classNames ?? []),
    ]}
    {...restProps}>
    {children}
  </ActionsButton>
);
export const SecondaryActionsButton = ({ classNames, children, ...restProps }) => (
  <ActionsButton
    classNames={[
      "secondary active:scale-90 bg-secondary-secondary_button text-[#3379db] hover:bg-secondary-secondary_button_hover",
      ...(classNames ?? []),
    ]}
    {...restProps}>
    {children}
  </ActionsButton>
);
export const NegativeActionsButton = ({ classNames, children, ...restProps }) => (
  <ActionsButton
    classNames={[
      "negative active:scale-90 text-red-500 bg-secondary-negative_button_bg hover:bg-secondary-negative_button_hover",
      ...(classNames ?? []),
    ]}
    {...restProps}>
    {children}
  </ActionsButton>
);
export const PositiveActionsButton = ({ classNames, children, ...restProps }) => (
  <ActionsButton
    classNames={[
      "positive active:scale-90 bg-secondary-positive_button_bg text-green-700",
      ...(classNames ?? []),
    ]}
    {...restProps}>
    {children}
  </ActionsButton>
);

export const PrimaryButton = ({ children, action, ...restProps }) => (
  <PrimaryActionsButton actions={[{ action: action, content: children }]} {...restProps} />
);
export const SecondaryButton = ({ children, action, ...restProps }) => (
  <SecondaryActionsButton actions={[{ action: action, content: children }]} {...restProps} />
);
export const NegativeButton = ({ children, action, ...restProps }) => (
  <NegativeActionsButton actions={[{ action: action, content: children }]} {...restProps} />
);
export const PositiveButton = ({ children, action, ...restProps }) => (
  <PositiveActionsButton actions={[{ action: action, content: children }]} {...restProps} />
);
